import { Controller } from "@hotwired/stimulus"
import { fromEvent, merge } from "rxjs";
import { debounceTime, distinctUntilChanged, map, scan } from "rxjs/operators";

export default class extends Controller {
  static targets = ['search', 'assignee', 'escalation', 'lender', 'valuer',
                    'broker', 'reportType', 'valuerRag', 'auditorName',
                    'priorityFlag', 'quoteStatus', 'content', 'labels', 'toggle' ]

  toggle() {
    this.expanded = !this.expanded;
    this.syncVisibility();
  }

  syncVisibility() {
    if (this.hasContentTarget) {
      this.contentTarget.classList.toggle('hidden', !this.expanded);
      this.contentTarget.setAttribute('aria-hidden', String(!this.expanded));
    }
    this.toggleTargets.forEach((el) => el.setAttribute('aria-expanded', String(this.expanded)));
  }

  initialize() {
    if (this.hasSearchTarget) {
      this.searchChange$ = fromEvent(this.searchTarget, "keyup").pipe(
        map((e) => e.target.value.toLowerCase()),
        map(el => ({ser: el})),
        debounceTime(500),
        distinctUntilChanged());
    }

    if (this.hasEscalationTarget) {
      this.escalationChange$ = fromEvent(this.escalationTarget, "change")
        .pipe(
          map((e) => e.target.value),
          map(el => ({esc: el}))
        );
    }

    if (this.hasAssigneeTarget) {
      this.assigneeChange$ = fromEvent(this.assigneeTarget, "change")
        .pipe(
          map((e) => e.target.value),
          map(el => ({ass: el}))
        );
    }

    if (this.hasLabelsTarget) {
      // Use document.querySelectorAll because checkboxes are in a portal (outside from element)
      this.labelChange$ = fromEvent(this.labelsTarget, "change")
        .pipe(
          map(() => {
            const checked = document.querySelectorAll('input[type="checkbox"][name="label_filter[]"]:checked');
            return Array.from(checked).map(input => input.dataset.value);
          }),
          map(el => ({lab: el}))
        );
    }

    if (this.hasLenderTarget) {
      this.lenderChange$ = fromEvent(this.lenderTarget, "change")
        .pipe(
          map((e) => e.target.value),
          map(el => ({len: el}))
        );
    }

    if (this.hasValuerTarget) {
      this.valuerChange$ = fromEvent(this.valuerTarget, "change")
        .pipe(
          map((e) => e.target.value),
          map(el => ({valFirm: el}))
        );
    }

    if (this.hasBrokerTarget) {
      this.brokerChange$ = fromEvent(this.brokerTarget, "change")
        .pipe(
          map((e) => e.target.value),
          map(el => ({bro: el}))
        );
    }

    if (this.hasReportTypeTarget) {
      this.reportTypeChange$ = fromEvent(this.reportTypeTarget, "change")
        .pipe(
          map((e) => e.target.value),
          map(el => ({reportType: el}))
        );
    }

    if (this.auditorNameTargets.length > 0) {
      this.auditorNameChange$ = fromEvent(this.auditorNameTarget, "change")
        .pipe(
            map((e) => e.target.value),
            map(el => ({auditorName: el}))
        );
    }

    if (this.valuerRagTargets.length > 0) {
      this.valuerRagChange$ = fromEvent(this.valuerRagTarget, "change")
        .pipe(
            map((e) => e.target.value),
            map(el => ({ragRating: el}))
        );
    }

    if (this.priorityFlagTargets.length > 0) {
      this.priorityFlagChange$ = fromEvent(this.priorityFlagTarget, "change")
          .pipe(
              map((e) => e.target.value),
              map(el => ({priorityFlag: el}))
          );
    }

    if (this.quoteStatusTargets.length > 0) {
      this.quoteStatusChange$ = fromEvent(this.quoteStatusTarget, "change")
        .pipe(
          map((e) => e.target.value),
          map(el => ({quoteStatus: el}))
        );
    }
  }

  connect() {
    this.expanded = false;

    let observers = [
      this.searchChange$,
      this.escalationChange$,
      this.assigneeChange$,
      this.labelChange$,
      this.lenderChange$,
      this.valuerChange$,
      this.brokerChange$,
      this.reportTypeChange$,
      this.auditorNameChange$,
      this.valuerRagChange$,
      this.priorityFlagChange$,
      this.quoteStatusChange$
    ]
    this.subscription = merge.apply(this, observers.filter((v) => v !== undefined))
      .pipe(
        scan((acc, curr) => Object.assign({}, acc, curr), {})
      ).subscribe((v) => {
      document.dispatchEvent(new CustomEvent('board_list_event_change', {detail: {filter: v}}));
    });
  }

  disconnect() {
    this.subscription?.unsubscribe();
  }
}
